/**
 * EmailThreadItem — renders a single email / thread rollup item for the
 * Communications timeline and the triage inbox (EDG-8483, plan item 9).
 *
 * Display-only: subject, preview, direction, sender, time, message count,
 * attachment hint, and an "Open in Outlook" link. Callers inject interactive
 * controls (selection checkbox, inclusion toggle) through the `actions` slot so
 * this component stays reusable across surfaces. Styling holds to the design
 * tokens (font ≤20px, radius ≤6px, token colours).
 */

import type { ReactNode } from "react";
import { ArrowDownLeft, ArrowUpRight, ExternalLink, Mail, Paperclip } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/** The email-shaped fields shared by feed items and triage items. */
export interface EmailItemLike {
  id: string;
  threadId: string | null;
  occurredAt: string;
  subject: string | null;
  preview: string | null;
  direction: "inbound" | "outbound" | null;
  messageCount?: number;
  hasAttachments?: boolean;
  outlookWebLink?: string | null;
  from?: unknown;
}

interface EmailThreadItemProps {
  item: EmailItemLike;
  /** Interactive controls rendered in the item's top-right (checkbox, toggle). */
  actions?: ReactNode;
  /** Controls rendered under the body (e.g. per-link inclusion in triage). */
  footer?: ReactNode;
  className?: string;
  testId?: string;
}

/** Display name for the sender from its `from` participant jsonb. */
export function emailFromName(from: unknown): string | null {
  if (!from || typeof from !== "object") return null;
  const address = (from as { emailAddress?: { address?: string; name?: string | null } })
    .emailAddress;
  return address?.name ?? address?.address ?? null;
}

function formatOccurredAt(value: string) {
  return new Date(value).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function EmailThreadItem({
  item,
  actions,
  footer,
  className,
  testId = "email-thread-item",
}: EmailThreadItemProps) {
  const isThread = Boolean(item.threadId) && (item.messageCount ?? 1) > 1;
  const sender = emailFromName(item.from);
  const DirectionIcon = item.direction === "outbound" ? ArrowUpRight : ArrowDownLeft;

  return (
    <div
      className={cn("rounded-md border bg-card p-3", className)}
      data-testid={testId}
      data-thread-id={item.threadId ?? undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Badge variant="outline" className="gap-1 capitalize" data-testid={`${testId}-direction`}>
            <DirectionIcon className="h-3 w-3" />
            {item.direction ?? "email"}
          </Badge>
          <span className="truncate text-sm font-medium" data-testid={`${testId}-subject`}>
            {item.subject || "(no subject)"}
          </span>
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>

      <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
        {sender ? <span className="truncate">{sender}</span> : null}
        {sender ? <span>·</span> : null}
        <span>{formatOccurredAt(item.occurredAt)}</span>
        {isThread ? (
          <Badge variant="secondary" className="h-4 px-1.5" data-testid={`${testId}-count`}>
            {item.messageCount} messages
          </Badge>
        ) : null}
        {item.hasAttachments ? (
          <Paperclip className="h-3 w-3" aria-label="Has attachments" data-testid={`${testId}-attachment`} />
        ) : null}
      </div>

      {item.preview ? (
        <p className="mt-2 line-clamp-2 text-sm text-foreground/90" data-testid={`${testId}-preview`}>
          {item.preview}
        </p>
      ) : null}

      {item.outlookWebLink ? (
        <a
          href={item.outlookWebLink}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
          data-testid={`${testId}-outlook-link`}
        >
          <ExternalLink className="h-3 w-3" />
          Open in Outlook
        </a>
      ) : null}

      {footer ? <div className="mt-2 border-t pt-2">{footer}</div> : null}
    </div>
  );
}
